import type { HeroTheme } from "@scrile/contracts";
import { contrast, hslToRgb, luminance, mix, parseColor, rgbToHsl, toHex, withContrast, type Rgb } from "./lib/color";

/**
 * Тема героя → CSS-переменные на :root.
 *
 * Креатор задаёт одну палитру (фон, поверхность, текст, акцент). Это его «родная» схема:
 * у шефа — тёмная кухня ночью. Вторая схема выводится из неё: тот же тон и акцент, но
 * светлые фон и поверхность (или наоборот). Какую показать, решает prefers-color-scheme
 * устройства — человек с телефоном в светлом режиме не получает внезапно чёрный экран,
 * а тёмная ночью не слепит.
 *
 * Все производные цвета проверяются на контраст WCAG: креатор может выбрать что угодно,
 * а текст обязан читаться.
 */

export type SchemeName = "light" | "dark";

export interface SchemeVars {
  "--bg": string;
  /** Фон с нулевой прозрачностью: градиент «обложка → фон» без серой полосы посередине. */
  "--bg-fade": string;
  "--surface": string;
  "--surface-2": string;
  "--text": string;
  "--muted": string;
  "--line": string;
  "--accent": string;
  "--accent-text": string;
  "--accent-soft": string;
  "--on-accent": string;
  "--shadow": string;
  "--overlay": string;
}

export interface ThemeResult {
  base: SchemeName;
  light: SchemeVars;
  dark: SchemeVars;
  fontDisplay: string;
}

const FALLBACK = {
  background: { r: 22, g: 17, b: 14 },
  surface: { r: 33, g: 25, b: 21 },
  text: { r: 243, g: 230, b: 216 },
  accent: { r: 224, g: 137, b: 74 },
} satisfies Record<string, Rgb>;

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

interface Palette {
  bg: Rgb;
  surface: Rgb;
  text: Rgb;
  accent: Rgb;
}

/** Приглушённый текст: как можно тише, но с контрастом не ниже 4.8 (мелкий текст, WCAG AA). */
function mutedText(text: Rgb, bg: Rgb): Rgb {
  for (let weight = 0.5; weight > 0.05; weight -= 0.05) {
    const candidate = mix(text, bg, weight);
    if (contrast(candidate, bg) >= 4.8) return candidate;
  }
  return text;
}

function schemeVars(palette: Palette, scheme: SchemeName): SchemeVars {
  const { bg, surface } = palette;
  // Текст креатора, если читается; иначе чёрный или белый — что контрастнее
  const text =
    contrast(palette.text, bg) >= 4.5 ? palette.text : contrast(WHITE, bg) >= contrast(BLACK, bg) ? WHITE : BLACK;
  const accent = withContrast(palette.accent, bg, 3);
  const accentText = withContrast(palette.accent, bg, 4.5);
  const darkInk = luminance(text) < luminance(bg) ? text : bg;
  // Белый на кнопке, если читается; иначе самый тёмный цвет темы — тёплый, а не #000
  const onAccent = contrast(WHITE, accent) >= 4.5 ? WHITE : contrast(darkInk, accent) >= contrast(WHITE, accent) ? darkInk : WHITE;
  const hue = rgbToHsl(bg).h;
  return {
    "--bg": toHex(bg),
    "--bg-fade": toRgba(bg, 0),
    "--surface": toHex(surface),
    "--surface-2": toHex(mix(surface, text, scheme === "dark" ? 0.07 : 0.045)),
    "--text": toHex(text),
    "--muted": toHex(mutedText(text, bg)),
    "--line": toHex(mix(bg, text, scheme === "dark" ? 0.15 : 0.13)),
    "--accent": toHex(accent),
    "--accent-text": toHex(accentText),
    "--accent-soft": toHex(mix(bg, accent, scheme === "dark" ? 0.18 : 0.13)),
    "--on-accent": toHex(onAccent),
    "--shadow":
      scheme === "dark"
        ? "0 18px 40px rgba(0, 0, 0, 0.45)"
        : `0 14px 34px ${toRgba(hslToRgb({ h: hue, s: 0.35, l: 0.25 }), 0.12)}`,
    "--overlay": scheme === "dark" ? "rgba(0, 0, 0, 0.6)" : toRgba(hslToRgb({ h: hue, s: 0.3, l: 0.12 }), 0.42),
  };
}

function toRgba({ r, g, b }: Rgb, alpha: number): string {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${alpha})`;
}

/** Светлая пара для тёмной темы креатора: тот же тон, бумажный фон, его фон — текстом. */
function deriveLight(base: Palette): Palette {
  const tone = rgbToHsl(base.bg).s > 0.06 ? rgbToHsl(base.bg) : rgbToHsl(base.accent);
  const saturation = clamp(tone.s, 0.18, 0.42);
  const bg = hslToRgb({ h: tone.h, s: saturation, l: 0.955 });
  const surface = hslToRgb({ h: tone.h, s: saturation, l: 0.988 });
  const text = contrast(base.bg, bg) >= 12 ? base.bg : hslToRgb({ h: tone.h, s: clamp(tone.s, 0.1, 0.35), l: 0.1 });
  return { bg, surface, text, accent: base.accent };
}

/** Тёмная пара для светлой темы креатора: ночь того же тона, его фон — текстом. */
function deriveDark(base: Palette): Palette {
  const tone = rgbToHsl(base.bg).s > 0.06 ? rgbToHsl(base.bg) : rgbToHsl(base.accent);
  const saturation = clamp(tone.s, 0.14, 0.3);
  const bg = hslToRgb({ h: tone.h, s: saturation, l: 0.075 });
  const surface = hslToRgb({ h: tone.h, s: saturation, l: 0.115 });
  const text = contrast(base.bg, bg) >= 12 ? base.bg : hslToRgb({ h: tone.h, s: 0.25, l: 0.92 });
  return { bg, surface, text, accent: base.accent };
}

export function buildTheme(theme: HeroTheme): ThemeResult {
  const palette: Palette = {
    bg: parseColor(theme.background) ?? FALLBACK.background,
    surface: parseColor(theme.surface) ?? FALLBACK.surface,
    text: parseColor(theme.text) ?? FALLBACK.text,
    accent: parseColor(theme.accent) ?? FALLBACK.accent,
  };
  const base: SchemeName = luminance(palette.bg) < 0.2 ? "dark" : "light";
  const light = base === "light" ? palette : deriveLight(palette);
  const dark = base === "dark" ? palette : deriveDark(palette);
  return {
    base,
    light: schemeVars(light, "light"),
    dark: schemeVars(dark, "dark"),
    fontDisplay: fontStack(theme.font),
  };
}

function block(vars: SchemeVars, scheme: SchemeName): string {
  const lines = Object.entries(vars).map(([name, value]) => `${name}: ${value};`);
  return `${lines.join(" ")} color-scheme: ${scheme};`;
}

/** CSS для <style>: светлая схема по умолчанию, тёмная — по prefers-color-scheme. */
export function themeCss(result: ThemeResult): string {
  return [
    `:root { ${block(result.light, "light")} --font-display: ${result.fontDisplay}; }`,
    `@media (prefers-color-scheme: dark) { :root { ${block(result.dark, "dark")} } }`,
  ].join("\n");
}

// --- шрифт ----------------------------------------------------------------------

const SYSTEM_FONTS = new Set([
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-rounded",
  "ui-monospace",
  "serif",
  "sans-serif",
  "monospace",
  "-apple-system",
  "georgia",
  "times new roman",
  "times",
  "arial",
  "helvetica",
  "helvetica neue",
  "verdana",
  "trebuchet ms",
  "palatino",
  "baskerville",
  "iowan old style",
  "avenir",
  "avenir next",
  "segoe ui",
  "menlo",
  "courier new",
]);

const SERIF_FALLBACK = '"Iowan Old Style", "Palatino Linotype", Georgia, ui-serif, serif';
const SANS_FALLBACK = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

/** Имя семейства без кавычек и спецсимволов: оно попадает в CSS и в адрес Google Fonts. */
export function cleanFamily(family: string): string {
  return family.replace(/[^\p{L}\p{N} -]/gu, "").replace(/\s+/g, " ").trim();
}

export function isSystemFont(family: string): boolean {
  return SYSTEM_FONTS.has(cleanFamily(family).toLowerCase());
}

function looksSerif(family: string): boolean {
  const name = family.toLowerCase();
  if (name.includes("sans")) return false;
  return /serif|garamond|baskerville|caslon|playfair|fraunces|lora|merriweather|cormorant|crimson|spectral|bodoni|didot|abril|prata|cardo|gelasio|literata|newsreader|georgia|palatino|times/.test(
    name,
  );
}

/** "Fraunces" → '"Fraunces", "Iowan Old Style", …, serif' — запасные того же рода. */
export function fontStack(family: string): string {
  const name = cleanFamily(family);
  const fallback = looksSerif(name) ? SERIF_FALLBACK : SANS_FALLBACK;
  if (!name) return fallback;
  return isSystemFont(name) ? `${name.includes(" ") ? `"${name}"` : name}, ${fallback}` : `"${name}", ${fallback}`;
}

/**
 * Адрес Google Fonts. Сначала с нужными насыщенностями; у семейства без 600 такой
 * запрос вернёт 400, и loadFont повторит его без насыщенностей.
 */
export function googleFontsHref(family: string, withWeights: boolean): string {
  const name = encodeURIComponent(cleanFamily(family)).replace(/%20/g, "+");
  return `https://fonts.googleapis.com/css2?family=${name}${withWeights ? ":wght@400;500;600" : ""}&display=swap`;
}

function loadFont(family: string, doc: Document): void {
  const name = cleanFamily(family);
  if (!name || isSystemFont(name)) return;
  const id = "hero-font";
  if (doc.getElementById(id)?.getAttribute("data-family") === name) return;
  doc.getElementById(id)?.remove();

  for (const origin of ["https://fonts.googleapis.com", "https://fonts.gstatic.com"]) {
    if (doc.head.querySelector(`link[rel="preconnect"][href="${origin}"]`)) continue;
    const preconnect = doc.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = origin;
    if (origin.includes("gstatic")) preconnect.crossOrigin = "anonymous";
    doc.head.appendChild(preconnect);
  }

  const link = doc.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = googleFontsHref(name, true);
  link.setAttribute("data-family", name);
  link.addEventListener(
    "error",
    () => {
      // Нет такой насыщенности — берём семейство как есть; нет сети — останутся запасные
      link.href = googleFontsHref(name, false);
    },
    { once: true },
  );
  doc.head.appendChild(link);
}

// --- применение -----------------------------------------------------------------

function setMeta(doc: Document, name: string, content: string, media?: string): void {
  const meta = doc.createElement("meta");
  meta.name = name;
  meta.content = content;
  if (media) meta.media = media;
  doc.head.appendChild(meta);
}

function setLink(doc: Document, rel: string, href: string): void {
  let link = doc.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = doc.createElement("link");
    link.rel = rel;
    doc.head.appendChild(link);
  }
  link.removeAttribute("type");
  link.href = href;
}

/** Растровая картинка — для apple-touch-icon iOS берёт только такие. */
function isRaster(url: string): boolean {
  return /\.(png|jpe?g|webp)(\?|#|$)/i.test(url) || /^data:image\/(png|jpe?g|webp)/i.test(url);
}

/**
 * Применить тему: переменные, цвет строки состояния, шрифт, иконка и имя для «На экран
 * „Домой“». Манифест PWA статический и общий на все поддомены — имя героя в нём
 * может выставить только сервер (см. отчёт).
 */
export function applyTheme(theme: HeroTheme, heroName: string, doc: Document = document): ThemeResult {
  const result = buildTheme(theme);
  let style = doc.getElementById("hero-theme");
  if (!style) {
    style = doc.createElement("style");
    style.id = "hero-theme";
    doc.head.appendChild(style);
  }
  style.textContent = themeCss(result);

  doc.head.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.remove());
  setMeta(doc, "theme-color", result.light["--bg"], "(prefers-color-scheme: light)");
  setMeta(doc, "theme-color", result.dark["--bg"], "(prefers-color-scheme: dark)");
  doc.head.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", heroName);

  const icon = theme.logoUrl ?? theme.portraitUrl;
  if (icon) {
    setLink(doc, "icon", icon);
    if (isRaster(icon)) setLink(doc, "apple-touch-icon", icon);
  }

  loadFont(theme.font, doc);
  return result;
}
